/**
 * Hook para tema ACV en GanGen (mismo patrón que aiRecording/useExtensionTheme)
 * Carga tema desde API acvapp y aplica variables CSS en document.documentElement
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { ThemesApiClient } from '../lib/themesApi';

const MODULE_ID = 'gan' as const;

const colorToRgb = (color: string): { r: number; g: number; b: number } | null => {
  if (!color) return null;
  if (color.startsWith('var(')) {
    const varMatch = color.match(/var\(([^,)]+)/);
    if (varMatch) {
      const computed = getComputedStyle(document.documentElement).getPropertyValue(varMatch[1].trim()).trim();
      if (computed) color = computed;
    }
  }
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    const a = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1;
    if (a < 0.5) {
      return {
        r: Math.round(r * a + 255 * (1 - a)),
        g: Math.round(g * a + 255 * (1 - a)),
        b: Math.round(b * a + 255 * (1 - a)),
      };
    }
    return { r, g, b };
  }
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }
  return null;
};

const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((v) => {
    v = v / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getOptimalTextColor = (bg: string): string => {
  const rgb = colorToRgb(bg);
  if (!rgb) return '#1a1a1a';
  const lum = getLuminance(rgb.r, rgb.g, rgb.b);
  const avg = (rgb.r + rgb.g + rgb.b) / 3;
  if (avg < 50) return '#ffffff';
  return lum > 0.5 ? '#1a1a1a' : '#f5f5f5';
};

export const useExtensionTheme = () => {
  const [theme, setTheme] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedInitialTheme = useRef(false);

  const applyThemeColors = useCallback((themeData: any, _source: string = 'unknown') => {
    if (!themeData?.colors) return;
    const root = document.documentElement;
    const colors = themeData.colors;
    const family = themeData.family || 'conservative';

    root.setAttribute('data-theme-family', family);

    root.style.setProperty('--theme-background', colors.background || '#ffffff');
    root.style.setProperty('--theme-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--theme-card', colors.card || '#ffffff');
    root.style.setProperty('--theme-navbar', colors.navbar || '#ffffff');
    root.style.setProperty('--theme-toolbar', colors.toolbar || colors.navbar || '#ffffff');
    root.style.setProperty('--theme-border', colors.border || '#e0e0e0');
    root.style.setProperty('--theme-primary', colors.primary || '#1976d2');
    root.style.setProperty('--theme-muted', colors.muted || '#f5f5f5');
    root.style.setProperty('--theme-destructive', colors.error || '#f44336');

    root.style.setProperty('--bg-primary', colors.background || '#ffffff');
    root.style.setProperty('--text-primary', colors.foreground || '#1a1a1a');
    root.style.setProperty('--text-secondary', colors.muted || '#666666');
    root.style.setProperty('--bg-alternate', colors.card || '#f8f8f8');

    const surfaceBg = family === 'playful' ? (colors.background || '#ffffff') : (colors.card || '#ffffff');
    root.style.setProperty('--surface-base', surfaceBg);
    root.style.setProperty('--surface-base-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--card-bg', family === 'playful' ? (colors.background || '#ffffff') : (colors.card || '#ffffff'));
    root.style.setProperty('--card-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--card-title-color', colors.foreground || '#1a1a1a');
    root.style.setProperty('--card-text-color', colors.muted || '#666666');
    root.style.setProperty('--card-border', `1px solid ${colors.border || '#e0e0e0'}`);
    root.style.setProperty('--border-color', colors.border || '#e0e0e0');

    const btnFg = family === 'playful' ? '#000000' : (colors.buttonPrimaryForeground || '#ffffff');
    root.style.setProperty('--button-primary-bg', colors.primary || '#1976d2');
    root.style.setProperty('--button-primary-foreground', btnFg);
    root.style.setProperty('--button-primary-hover-bg', colors.primaryHover || colors.primary || '#1565c0');
    root.style.setProperty('--button-secondary-foreground', colors.primary || '#1976d2');
    root.style.setProperty('--button-secondary-border', `1px solid ${colors.primary || '#1976d2'}`);

    root.style.setProperty('--input-bg', colors.card || '#ffffff');
    root.style.setProperty('--input-foreground', getOptimalTextColor(colors.card || '#ffffff'));
    root.style.setProperty('--input-border', `1px solid ${colors.border || '#e0e0e0'}`);

    const navbarBg = family === 'playful' ? (colors.background || '#ffffff') : (colors.navbar || colors.background || '#ffffff');
    root.style.setProperty('--navbar-bg', navbarBg);
    root.style.setProperty('--navbar-foreground', colors.foreground || '#1a1a1a');

    const sidebarBg = family === 'playful' ? (colors.background || '#ffffff') : (colors.sidebarBg || colors.card || '#ffffff');
    root.style.setProperty('--sidebar-bg', sidebarBg);
    root.style.setProperty('--sidebar-foreground', colors.sidebarForeground || colors.foreground || '#1a1a1a');
    root.style.setProperty('--sidebar-border', `1px solid ${colors.sidebarBorder || colors.border || '#e0e0e0'}`);
    root.style.setProperty('--sidebar-item-selected-bg', colors.sidebarItemSelectedBg || colors.primary || '#1976d2');
    root.style.setProperty('--sidebar-item-selected-foreground', family === 'playful' ? '#000000' : '#ffffff');

    const controlsBg = family === 'playful' ? (colors.background || '#ffffff') : (colors.controlsBg || colors.navbar || colors.card || '#ffffff');
    const controlsFgResolved = getOptimalTextColor(controlsBg);
    root.style.setProperty('--controls-bg', controlsBg);
    root.style.setProperty('--controlsBg', controlsBg);
    root.style.setProperty('--controls-foreground', controlsFgResolved);
    root.style.setProperty('--controlsForeground', controlsFgResolved);
    root.style.setProperty('--controls-border', colors.controlsBorder || colors.border || '#d6d6d6');

    root.style.setProperty('--color-primary', colors.primary || '#1976d2');
    root.style.setProperty('--color-error', colors.error || '#f44336');
    root.style.setProperty('--color-success', colors.success || '#4caf50');

    if (themeData.typography) {
      const fontMap: Record<string, string> = {
        Arial: '"Arial", sans-serif',
        Roboto: '"Roboto", "Helvetica", "Arial", sans-serif',
        'Comic Sans': '"Comic Sans MS", cursive',
        Serif: '"Georgia", "Times New Roman", serif',
        'Sans-Serif': '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        Monospace: '"Courier New", "Courier", monospace',
      };
      const fontFamily = fontMap[themeData.typography.fontFamily] || fontMap['Sans-Serif'];
      const fontSize = `${themeData.typography.fontSizeBase || 16}px`;
      root.style.setProperty('--font-family-base', fontFamily);
      root.style.setProperty('--font-size-base', fontSize);
      if (document.body) {
        document.body.style.fontFamily = fontFamily;
        document.body.style.fontSize = fontSize;
      }
    }
  }, []);

  const loadTheme = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const apiTheme = await ThemesApiClient.getModuleTheme(MODULE_ID);
      if (apiTheme?.id) {
        setTheme(apiTheme);
        applyThemeColors(apiTheme, 'database_api');
        setIsLoading(false);
        return;
      }
      const moduleThemesStr = localStorage.getItem('moduleThemes');
      if (moduleThemesStr) {
        try {
          const moduleThemes = JSON.parse(moduleThemesStr);
          const moduleTheme = moduleThemes[MODULE_ID];
          const themeId = typeof moduleTheme === 'string' ? moduleTheme : moduleTheme?.themeId;
          if (themeId) {
            const themeById = await ThemesApiClient.getThemeById(themeId);
            if (themeById?.id) {
              setTheme(themeById);
              applyThemeColors(themeById, 'acv_localStorage');
              setIsLoading(false);
              return;
            }
          }
        } catch (_) {}
      }
      const ownId = localStorage.getItem(`${MODULE_ID}_themeId`);
      if (ownId) {
        const themeById = await ThemesApiClient.getThemeById(ownId);
        if (themeById?.id) {
          setTheme(themeById);
          applyThemeColors(themeById, 'own_localStorage');
          setIsLoading(false);
          return;
        }
      }
      setIsLoading(false);
    } catch (err: any) {
      setError(err?.message || 'Error loading theme');
      setIsLoading(false);
    }
  }, [applyThemeColors]);

  const applyTheme = useCallback(
    (themeData: any, source: string = 'manual') => {
      if (!themeData) return;
      setTheme(themeData);
      applyThemeColors(themeData, source);
      if (themeData.id) localStorage.setItem(`${MODULE_ID}_themeId`, themeData.id);
    },
    [applyThemeColors]
  );

  useEffect(() => {
    if (!hasLoadedInitialTheme.current) {
      hasLoadedInitialTheme.current = true;
      loadTheme();
    }
  }, [loadTheme]);

  useEffect(() => {
    const handleThemeCommand = async (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const detail = customEvent.detail || {};
      const type = detail?.type;
      const themeId =
        detail?.themeId ||
        detail?.theme?.id ||
        detail?.payload?.themeId ||
        detail?.message?.themeId;
      const incomingTheme =
        detail?.theme ||
        detail?.payload?.theme ||
        detail?.message?.theme ||
        null;

      if (
        (type === 'theme-change' || type === 'APPLY_THEME') &&
        incomingTheme?.colors
      ) {
        applyTheme(
          {
            ...incomingTheme,
            id: incomingTheme.id || themeId || `runtime-${Date.now()}`,
          },
          'postMessage-theme-object'
        );
        return;
      }

      if ((type === 'theme-change' || type === 'APPLY_THEME') && themeId) {
        const apiTheme = await ThemesApiClient.getThemeById(themeId);
        if (apiTheme?.id) applyTheme(apiTheme, 'postMessage-theme-id');
      } else if (type === 'theme-reload') {
        hasLoadedInitialTheme.current = false;
        loadTheme();
      }
    };
    window.addEventListener('extension-theme-command', handleThemeCommand);
    return () => window.removeEventListener('extension-theme-command', handleThemeCommand);
  }, [applyTheme, loadTheme]);

  return { theme, isLoading, error, applyTheme, reloadTheme: loadTheme };
};
